import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Draft } from '@vigie/shared';
import { api } from '../api.js';
import { Card, Field, Notice, Score, StatusBadge } from '../components.js';
import { formatDate, useAction, useAsync } from '../hooks.js';

export function HitDetail({ onChanged }: { onChanged: () => void }) {
  const { hitId = '' } = useParams();
  const detail = useAsync(() => api.hit(hitId), [hitId]);
  const { run, busy, error } = useAction();

  const [content, setContent] = useState('');
  const [override, setOverride] = useState('');
  const [postUrl, setPostUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);

  useEffect(() => {
    const latest = detail.data?.drafts[0] ?? null;
    setDraft(latest);
    setContent(latest?.content ?? '');
  }, [detail.data]);

  if (detail.error) {
    return (
      <div className="content">
        <Notice kind="error">{detail.error}</Notice>
        <Link to="/inbox">← Retour à l’inbox</Link>
      </div>
    );
  }
  if (!detail.data) return <div className="content muted">Chargement…</div>;

  const { hit, posts } = detail.data;
  const post = posts[0] ?? null;

  const generate = async () => {
    const created = await run(() => api.createDraft(hit.id, override || undefined));
    if (created) {
      setDraft(created);
      setContent(created.content);
      detail.reload();
    }
  };

  const save = async () => {
    if (!draft) return;
    const updated = await run(() => api.updateDraft(draft.id, content));
    if (updated) setDraft(updated);
  };

  const approve = async () => {
    if (!draft) return;
    const approved = await run(async () => {
      await api.updateDraft(draft.id, content);
      return api.approveDraft(draft.id);
    });
    if (approved) {
      setDraft(approved);
      detail.reload();
      onChanged();
    }
  };

  const registerPost = async () => {
    const created = await run(() =>
      api.createPost({
        hitId: hit.id,
        draftId: draft?.id,
        url: postUrl || undefined,
        notes: notes || undefined,
      }),
    );
    if (created) {
      detail.reload();
      onChanged();
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <>
      <header className="topbar">
        <Link to="/inbox" className="button ghost">←</Link>
        <h1 className="truncate">{hit.title}</h1>
        <span className="spacer" />
        <Score score={hit.score} />
        <StatusBadge status={hit.status} />
      </header>

      <div className="content">
        {error ? <Notice kind="error">{error}</Notice> : null}

        <Notice kind="rule">
          Vigie ne publie jamais. Relisez le brouillon, copiez-le, publiez vous-même, puis revenez
          coller l’URL de votre réponse pour mesurer ce qu’elle rapporte.
        </Notice>

        <div className="detail-grid">
          <Card title="La discussion">
            <div className="stack">
              <div>
                <a href={hit.url} target="_blank" rel="noreferrer">
                  {hit.url} ↗
                </a>
              </div>
              <div className="small muted">
                Publiée le {formatDate(hit.postedAt)} · détectée le {formatDate(hit.discoveredAt)}
                {hit.author ? ` · par ${hit.author}` : ''}
                {hit.isManual ? ' · ajout manuel' : ''}
              </div>
              {hit.scoreReason ? (
                <div className="notice">
                  <strong>Score {hit.score}/10</strong> — {hit.scoreReason}
                </div>
              ) : null}
              <div style={{ whiteSpace: 'pre-wrap' }}>{hit.bodyExcerpt ?? '(pas de corps de message)'}</div>
            </div>
          </Card>

          <Card
            title="Brouillon"
            actions={
              <div className="inline">
                <button type="button" className="small" onClick={generate} disabled={busy}>
                  {draft ? 'Regénérer' : 'Générer'}
                </button>
                {draft ? (
                  <>
                    <button type="button" className="small" onClick={save} disabled={busy}>
                      Enregistrer
                    </button>
                    <button type="button" className="small primary" onClick={copy}>
                      {copied ? 'Copié ✓' : 'Copier'}
                    </button>
                  </>
                ) : null}
              </div>
            }
          >
            <Field
              label="Consigne ponctuelle (facultatif)"
              help="S’ajoute aux instructions du site, de la plateforme et de la source, et l’emporte sur elles."
            >
              <input
                value={override}
                placeholder="ex. réponse courte, pas de lien"
                onChange={(event) => setOverride(event.target.value)}
              />
            </Field>

            <textarea
              value={content}
              onChange={(event) => setContent(event.target.value)}
              placeholder="Générez un premier jet, puis relisez-le et corrigez-le avant de publier."
            />

            {draft ? (
              <div className="inline small muted" style={{ marginTop: 8 }}>
                <span>modèle {draft.model}</span>
                <span>·</span>
                <span>créé le {formatDate(draft.createdAt)}</span>
                {draft.approvedAt ? <span className="badge ok">approuvé</span> : (
                  <button type="button" className="small" onClick={approve} disabled={busy}>
                    Approuver
                  </button>
                )}
              </div>
            ) : null}

            {draft ? (
              <details style={{ marginTop: 12 }}>
                <summary className="small muted">Prompt exact envoyé au modèle</summary>
                <textarea className="mono" readOnly value={draft.promptUsed} style={{ marginTop: 8 }} />
              </details>
            ) : null}
          </Card>
        </div>

        <Card title="Publication">
          {post ? (
            <div className="stack">
              <Notice kind="success">Publication enregistrée le {formatDate(post.postedAt)}.</Notice>
              <Field label="URL à insérer dans votre réponse (déjà taguée)">
                <input readOnly value={post.trackingUrl} className="mono" onFocus={(e) => e.target.select()} />
              </Field>
              <div className="small muted">
                Campagne <span className="mono">{post.utmCampaign}</span> — les visites remonteront
                dans le tableau de bord après la synchronisation Umami.
              </div>
              {post.url ? (
                <a href={post.url} target="_blank" rel="noreferrer">
                  Voir la réponse publiée ↗
                </a>
              ) : null}
            </div>
          ) : (
            <div className="stack">
              <Field
                label="URL de la réponse publiée"
                help="Renseignez-la après avoir publié à la main. L’URL taguée à insérer est générée au moment de l’enregistrement."
              >
                <input
                  value={postUrl}
                  placeholder="https://www.reddit.com/r/.../comment/..."
                  onChange={(event) => setPostUrl(event.target.value)}
                />
              </Field>
              <Field label="Notes (facultatif)">
                <input value={notes} onChange={(event) => setNotes(event.target.value)} />
              </Field>
              <div>
                <button type="button" className="primary" onClick={registerPost} disabled={busy}>
                  Enregistrer la publication
                </button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}
